import { CalendarDays, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { listPortalEvents } from "../api/client";
import type { PortalEventRow } from "../api/types";
import {
  BrandMark,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusPill,
} from "../components/ui";
import { useBrandTheme } from "../lib/brandTheme";
import { formatRange } from "../lib/format";
import { useApi } from "../lib/useApi";

// `/` — every event the signed-in client user may view, grouped per client.
// Client-name section headers only appear when more than one client is
// visible (most users belong to exactly one).

export function EventsHome() {
  const { data, error, loading, reload } = useApi(listPortalEvents, []);

  // Brand-theme the whole page only when every visible event shares the
  // same client — a multi-client roster keeps the neutral portal chrome
  // rather than arbitrarily wearing one client's colors.
  const groups = groupByClient(data ?? []);
  useBrandTheme(
    groups.length === 1
      ? (groups[0].events.find((e) => e.company?.theme)?.company?.theme ?? null)
      : null,
  );

  if (loading) return <LoadingState label="Loading your events…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const events = data ?? [];
  if (events.length === 0) {
    return (
      <>
        <PageHeading agencyName={null} />
        <EmptyState
          title="No events yet"
          body="When your agency publishes an event for you, it will appear here."
        />
      </>
    );
  }

  const showHeaders = groups.length > 1;
  const agencyName = events[0]?.agencyName ?? null;

  return (
    <>
      <PageHeading agencyName={agencyName} />
      <div className="flex flex-col gap-10">
        {groups.map((group) => (
          <section key={group.key}>
            {showHeaders && (
              <div className="mb-4 flex items-baseline gap-3 border-b border-hairline pb-2">
                <h2 className="font-serif text-[19px] tracking-tight">{group.clientName}</h2>
                <span className="text-[12px] text-ink-faint">
                  {group.events.length} {group.events.length === 1 ? "event" : "events"}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {group.events.map((event) => (
                <EventCard key={event.eventId} event={event} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function PageHeading({ agencyName }: { agencyName: string | null }) {
  return (
    <div className="mb-8">
      <div className="eyebrow">Your events</div>
      <h1 className="mt-1 font-serif text-[34px] leading-none tracking-tight">Events</h1>
      {agencyName && (
        <p className="mt-1 text-[13.5px] text-ink-soft">Produced by {agencyName}</p>
      )}
    </div>
  );
}

interface ClientGroup {
  key: string;
  clientName: string;
  events: PortalEventRow[];
}

function groupByClient(events: PortalEventRow[]): ClientGroup[] {
  const groups = new Map<string, ClientGroup>();
  for (const event of events) {
    const key = event.clientId ?? "unassigned";
    let group = groups.get(key);
    if (!group) {
      group = { key, clientName: event.clientName ?? "Your events", events: [] };
      groups.set(key, group);
    }
    group.events.push(event);
  }
  return Array.from(groups.values());
}

function EventCard({ event }: { event: PortalEventRow }) {
  const accent = event.company?.accentColor ?? event.primaryColor ?? "#D5B56F";
  const place = [event.venueName, event.addressLocality, event.addressRegion]
    .filter(Boolean)
    .join(" · ");

  return (
    <Link
      to={`/events/${event.eventId}`}
      className="luxe-panel group overflow-hidden rounded-card transition-shadow duration-300 ease-ios hover:shadow-lift"
    >
      {event.coverImageUrl ? (
        <div className="aspect-[16/8] overflow-hidden bg-white/5">
          <img
            src={event.coverImageUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 ease-ios group-hover:scale-[1.02]"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="h-1.5" style={{ backgroundColor: accent }} aria-hidden />
      )}

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-[20px] leading-snug tracking-tight text-ink">
            {event.name}
          </h3>
          <StatusPill status={event.status} />
        </div>

        <div className="mt-3 flex flex-col gap-1.5 text-[13.5px] text-ink-soft">
          <span className="flex items-center gap-2">
            <CalendarDays size={14} className="shrink-0 text-champagne" />
            {formatRange(event.startsAt, event.endsAt)}
          </span>
          {place && (
            <span className="flex items-center gap-2">
              <MapPin size={14} className="shrink-0 text-champagne" />
              {place}
            </span>
          )}
        </div>

        {event.company && (event.company.logoUrl || event.company.name) && (
          <div className="mt-4 flex items-center gap-2 border-t border-hairline/60 pt-3">
            <BrandMark brand={event.company} />
            {event.company.name && (
              <span className="text-[12px] font-medium text-ink-soft">{event.company.name}</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
